import React, { useEffect, useState, useMemo } from 'react'
import styled from 'styled-components';
import { useDispatch } from 'react-redux'

import useFetch from '../utils/useFetch'
import { fetchProductsCategories, createOrEditCategory, deleteCategory, changeProductStatus, changeCategoryStatus, deleteProduct, fetchDetailGroups, updateCategoriesOrder } from '../redux/actions/products'
import { CategoryAccordion, NewCategoryModal, OrganizeMenuModal } from '../components/products';
import { Loading, LoadingOverlay, Button } from '../components/common';
import { showNotification } from '../redux/actions/ui';
import { useStore } from '../providers/store.provider';
import { SelectMultistore } from '../components/select-multistore/select-multistore.component';
import { SelectInput } from 'vqp-common';
import { exportMenuReport } from '../utils/exportMenuReport';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px;
  width: 100%;
`

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  gap: 12px;
  min-height: 40px;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
`

const FilterBarRight = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const FilterLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.textBold};
  flex-shrink: 0;
`

const SelectWrapper = styled.div`
  width: 250px;
  min-width: 250px;
  max-width: 250px;
  position: relative;
  flex-shrink: 0;
`

const AccordionList = styled.div`
  margin-bottom: 24px;
`

const ProductsPage = () => {

    const dispatch = useDispatch()
    const storeProvider = useStore();

    const [categories, setCategories] = useState()
    const [actionLoading, setActionLoading] = useState()
    const [isMultistore, setIsMultistore] = useState(storeProvider.getMultistoreState());
    const selectedMultistore = storeProvider.selectedMultistore

    useEffect(() => {
        setIsMultistore(storeProvider.getMultistoreState())
    }, [storeProvider])

    const request = useFetch(fetchProductsCategories, selectedMultistore?.id, setCategories)

    const [selectedCategoryId, setSelectedCategoryId] = useState(null)

    const categoryFilterOptions = useMemo(() => {
        if (!categories || !categories.length) return [{ value: null, name: 'Todas as categorias' }]
        return [
            { value: null, name: 'Todas as categorias' },
            ...categories.map((c) => ({ value: c.id, name: c.name })),
        ]
    }, [categories])

    const displayedCategories = useMemo(() => {
        if (!categories) return []
        if (selectedCategoryId == null) return categories
        return categories.filter((c) => c.id === selectedCategoryId)
    }, [categories, selectedCategoryId])

    const categoryFilterSelectedIndex = useMemo(() => {
        const idx = categoryFilterOptions.findIndex((o) => o.value === selectedCategoryId)
        return idx >= 0 ? idx : 0
    }, [categoryFilterOptions, selectedCategoryId])

    const handleCategoryFilterChange = (selectedIndex) => {
        const option = categoryFilterOptions[selectedIndex]
        setSelectedCategoryId(option ? option.value : null)
    }

    const [expandedIds, setExpandedIds] = useState(() => new Set())
    const [newCategoryModalOpen, setNewCategoryModalOpen] = useState(false)
    const [organizeModalOpen, setOrganizeModalOpen] = useState(false)
    const [organizeLoading, setOrganizeLoading] = useState(false)
    const [exportLoading, setExportLoading] = useState(false)

    useEffect(() => {
        if (displayedCategories && displayedCategories.length > 0) {
            setExpandedIds(new Set(displayedCategories.map((c) => c.id)))
        }
    }, [selectedCategoryId, categories])

    const toggleCategory = (categoryId) => {
        setExpandedIds((prev) => {
            const next = new Set(prev)
            if (next.has(categoryId)) next.delete(categoryId)
            else next.add(categoryId)
            return next
        })
    }

    const handleAddNewCategory = async (name) => {
        setActionLoading(true)
        setTimeout(async () => {
            const result = await dispatch(createOrEditCategory({ name, storeId: selectedMultistore?.id }))
            setActionLoading(false)
            if (result.success) {
                request.refetch()
            } else {
                dispatch(showNotification({
                    type: 'error',
                    text: result.error.message
                }))
            }
        }, 30)
    }

    const handleDeleteCategory = (category) => async () => {
        setActionLoading(true)
        setTimeout(async () => {
            const result = await dispatch(deleteCategory({ category, storeId: selectedMultistore?.id }))
            setActionLoading(false)
            if (result.success) {
                const newCategories = [...categories]
                const index = categories.findIndex(c => c.id === category)
                if (index > -1) {
                    newCategories.splice(index, 1)
                }
                setCategories(newCategories)
            } else {
                dispatch(showNotification({
                    type: 'error',
                    text: result.error.message
                }))
            }
        }, 30)
    }

    const handleDeleteProduct = async (productId, storeId) => {
        const result = await dispatch(deleteProduct({ productId, storeId }))
        if (result.success) {
            request.refetch()
        } else {
            dispatch(showNotification({
                type: 'error',
                text: result.error.message
            }))
        }
    }

    const handleChangeProductStatus = async (productId) => {
        const result = await dispatch(changeProductStatus({ productId, storeId: selectedMultistore?.id }))
        if (result.success) {
            request.refetch()
        } else {
            dispatch(showNotification({
                type: 'error',
                text: result.error.message
            }))
        }
    }

    const handleChangeCategoryStatus = async (categoryId) => {
        setActionLoading(true)
        try {
            const result = await dispatch(changeCategoryStatus({ categoryId }))
            if (result.success) {
                request.refetch()
                dispatch(showNotification({
                    type: 'success',
                    text: 'Status da categoria alterado com sucesso.',
                }))
            } else {
                dispatch(showNotification({
                    type: 'error',
                    text: result.error || 'Erro ao alterar status da categoria.',
                }))
            }
        } finally {
            setActionLoading(false)
        }
    }

    const handleExportReport = async () => {
        if (!categories || categories.length === 0) {
            dispatch(showNotification({ type: 'info', text: 'Não há dados do cardápio para exportar.' }))
            return
        }
        setExportLoading(true)
        try {
            const groupsResult = await dispatch(fetchDetailGroups(selectedMultistore?.id))
            const detailGroups = (groupsResult?.success && groupsResult?.data) ? groupsResult.data : []
            exportMenuReport(categories, detailGroups, 'relatorio-cardapio')
            dispatch(showNotification({ type: 'success', text: 'Relatório exportado com sucesso.' }))
        } finally {
            setExportLoading(false)
        }
    }

    const handleSaveOrganize = async (orderedCategories) => {
        setOrganizeLoading(true)
        const result = await dispatch(
            updateCategoriesOrder({
                categories: orderedCategories,
                products: [],
                storeId: selectedMultistore?.id,
            })
        )
        setOrganizeLoading(false)
        if (result.success) {
            setOrganizeModalOpen(false)
            request.refetch()
            dispatch(showNotification({ type: 'success', text: 'Ordem do cardápio salva com sucesso.' }))
        } else {
            dispatch(
                showNotification({
                    type: 'error',
                    text: result.error || 'Erro ao salvar a ordem do cardápio.',
                })
            )
        }
    }

    if (!categories && request.loading) {
        return <Loading />
    }

    return (
        <>
            {
                (!isMultistore || !(isMultistore && !selectedMultistore)) ? (
                    <>
                        <LoadingOverlay visible={actionLoading || request.loading} />
                        <Wrapper>
                            {categories && (
                                <>
                                    <FilterBar>
                                        <FilterLabel>Buscar por categoria:</FilterLabel>
                                        <SelectWrapper>
                                            <SelectInput
                                                options={categoryFilterOptions}
                                                selected={categoryFilterSelectedIndex}
                                                onSelect={handleCategoryFilterChange}
                                            />
                                        </SelectWrapper>
                                        <FilterBarRight>
                                            <Button
                                                className="export"
                                                onClick={handleExportReport}
                                                disabled={exportLoading || !categories?.length}
                                            >
                                                {exportLoading ? 'Exportando...' : 'Exportar relatório'}
                                            </Button>
                                            <Button
                                                className="export"
                                                onClick={() => setOrganizeModalOpen(true)}
                                                disabled={!categories?.length}
                                            >
                                                Organizar cardápio
                                            </Button>
                                            <Button
                                                className="primary"
                                                onClick={() => setNewCategoryModalOpen(true)}
                                            >
                                                Adicionar categoria
                                            </Button>
                                        </FilterBarRight>
                                    </FilterBar>
                                    <AccordionList>
                                        {displayedCategories.map((cat) => (
                                            <CategoryAccordion
                                                key={cat.id}
                                                id={cat.id}
                                                name={cat.name}
                                                status={cat.status}
                                                products={cat.products}
                                                isOpen={expandedIds.has(cat.id)}
                                                onToggle={() => toggleCategory(cat.id)}
                                                onDeleteCategory={handleDeleteCategory(cat.id)}
                                                onDeleteProduct={handleDeleteProduct}
                                                onChangeProductStatus={handleChangeProductStatus}
                                                onChangeCategoryStatus={handleChangeCategoryStatus}
                                                refetchCategories={request.refetch}
                                                storeId={selectedMultistore?.id}
                                            />
                                        ))}
                                    </AccordionList>
                                    <NewCategoryModal
                                        isOpen={newCategoryModalOpen}
                                        onClose={() => setNewCategoryModalOpen(false)}
                                        onAdd={handleAddNewCategory}
                                    />
                                    <OrganizeMenuModal
                                        isOpen={organizeModalOpen}
                                        categories={categories}
                                        onClose={() => setOrganizeModalOpen(false)}
                                        onSave={handleSaveOrganize}
                                        loading={organizeLoading}
                                    />
                                </>
                            )}
                        </Wrapper>
                    </>
                ) : (
                    <SelectMultistore />
                )
            }

        </>
    )
}

export default ProductsPage